import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from 'components/common/button';
import { filterIntakeFormSettings, isEmptyString } from 'utils/helper';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import {
  StateTypeLocalStorage,
  usePersistMHReducer,
} from 'reducer/medicalHistory';
import SearchableTextBoxAllergies from 'components/common/searchableTextBoxAllergies';
import MetaTag from 'components/meta';
import { NullablePatientDTO } from 'types/patients';
import Alert, { AlertVariant } from 'components/common/Alerts';
import isNil from 'lodash/isNil';
import AlertTitle from '@mui/material/AlertTitle';
import Tooltip from 'components/common/tooltip';

export const enum SelectReaction {
  allergic = 'allergic',
  drugReaction = 'drugReaction',
  NonDrugAllergic = 'NonDrugAllergic',
  nonDrugReaction = 'nonDrugReaction',
}

type OptionType = {
  value: string;
  label: string;
  reaction?: string;
  readOnly?: boolean;
};

type AllergyPageValue = {
  drugAllergic?: OptionType[];
  nonDrugAllergic?: OptionType[];
  noKnownDrugAllergies?: boolean;
};

type Props = {
  nextPage: number;
  currentPage: number;
  actionPage: string;
  nextOrSubmitButton: (nextPage: number, submit?: boolean) => void;
};

function AllergiesScreen({
  nextPage,
  nextOrSubmitButton,
  actionPage,
}: Props) {
  const intakeFormSettings = useSelector(
    (state: RootState) => state.intakeFormSettings.value,
  );
  const patient = useSelector(
    (state: RootState) => state.patient,
  ) as NullablePatientDTO;

  const [error, setError] = useState(false);
  const [state, dispatch] = usePersistMHReducer();

  // values
  const [drugAllergic, setDrugAllergic] = useState<OptionType[]>([]);
  const [nonDrugAllergic, setNonDrugAllergic] = useState<OptionType[]>([]);
  const [noKnownDrugAllergies, setNoKnownDrugAllergies] = useState(false);
  const [drugAllergySettingsOptions, setDrugAllergySettingsOptions] = useState<
    OptionType[]
  >([]);
  const [allergyReactionSettingsOptions, setAllergyReactionSettingsOptions] = useState<
  string[]
  >([]);
  const [nonDrugAllergySettingsOptions, setNonDrugAllergySettingsOptions] = useState<
  OptionType[]
  >([]);

  useEffect(() => {
    if (intakeFormSettings) {
      const drugAllergySettings = filterIntakeFormSettings(
        intakeFormSettings,
        'drugAllergySettings',
      );

      const optionDrugArray: OptionType[] = drugAllergySettings.map(
        (item: Record<string, string>) => ({ value: item.source, label: item.source }),
      );

      setDrugAllergySettingsOptions(optionDrugArray);

      const allergyReactionSettings = filterIntakeFormSettings(
        intakeFormSettings,
        'allergyReactionSettings',
      );

      const optionReactionArray: string[] = [
        'Select Reaction Type',
        ...allergyReactionSettings.map((item: Record<string, string>) => item.reaction)];

      setAllergyReactionSettingsOptions(optionReactionArray);

      // // Non Drug
      const nonDrugAllergySettings = filterIntakeFormSettings(
        intakeFormSettings,
        'nonDrugAllergySettings',
      );

      const optionNonDrugArray: OptionType[] = nonDrugAllergySettings.map(
        (item: Record<string, string>) => ({
          label: item.source,
          value: item.source,
        }),
      );

      setNonDrugAllergySettingsOptions(optionNonDrugArray);
    }
  }, []);

  const clickNextOrSubmit = async (nextPage: number) => {
    const payload = {
      drugAllergic,
      nonDrugAllergic,
      noKnownDrugAllergies,
    };

    const findNonReactionOfNonDrugAllergic = nonDrugAllergic.find(
      (item) => !item.reaction,
    );
    if (findNonReactionOfNonDrugAllergic) {
      return;
    }

    await dispatch({
      type: actionPage,
      name: 'Allergies',
      value: payload,
    });
    nextOrSubmitButton(nextPage, true);
  };

  const onChangeSelectAutoComplete = (
    name: SelectReaction,
    value: OptionType[],
  ) => {
    if (name === SelectReaction.allergic) {
      setDrugAllergic(value);
      if (value.length > 0) {
        setNoKnownDrugAllergies(false);
      }
    }
    if (name === SelectReaction.NonDrugAllergic) {
      setNonDrugAllergic(value);
    }
    setError(false);
  };

  useEffect(() => {
    if (state) {
      const allergies = state.find(
        (p: StateTypeLocalStorage) => p.name === actionPage,
      );
      const value = (allergies?.value ?? []) as AllergyPageValue | [];

      if (!Array.isArray(value)
      && (value.drugAllergic || value.nonDrugAllergic || value.noKnownDrugAllergies)) {
        if (value.drugAllergic) {
          setDrugAllergic(value.drugAllergic);
        }
        if (value.nonDrugAllergic) {
          setNonDrugAllergic(value.nonDrugAllergic);
        }
        setNoKnownDrugAllergies(Boolean(value.noKnownDrugAllergies));
      } else {
        const drugAllergic = patient?.drugAllergies?.map((item) => (
          {
            ...item,
            value: item.displayName ?? '',
            label: item.displayName ?? '',
            reaction: item.reaction ?? '',
            readOnly: true,
          }
        ));
        if (drugAllergic) {
          setDrugAllergic(drugAllergic);
        }
        setNoKnownDrugAllergies(Boolean(patient?.hasNoKnownDrugAllergies));
        const nonDrugAllergies = patient?.nonDrugAllergies?.map((item) => (
          {
            ...item,
            value: item.source ?? '',
            label: item.source ?? '',
            reaction: item.reaction ?? '',
            readOnly: true,
          }
        ));
        if (nonDrugAllergies) {
          setNonDrugAllergic(nonDrugAllergies);
        }
      }
    }
  }, []);

  const hasDosespotError = useMemo(() => !isNil(patient?.dosespotError), [patient]);
  const hasDrugAllergies = useMemo(() => drugAllergic.length > 0, [drugAllergic]);
  const isNoKnownDrugAllergiesDisabled = hasDrugAllergies || hasDosespotError;
  const noKnownDrugAllergiesTooltip = hasDrugAllergies
    ? 'Remove drug allergies to enable No Known Drug Allergies.'
    : undefined;

  return (
    <>
      <MetaTag title="Allergies" />
      <Box pb={3.5} mb={4}>
        <Box>
          <h2 className="heading-text mt-0">Allergies</h2>
          <h3 className="heading-sub-text">Medicine</h3>
          {error && (
            <p className="error-message">
              Please select at least one option.
            </p>
          )}
          <Stack>
            <Alert
              severity={AlertVariant.ERROR}
              open={hasDosespotError}
              className="mb-3 !w-full"
            >
              <AlertTitle
                className="!text-sm"
              >
                {patient?.dosespotError?.title ?? ''}
              </AlertTitle>
              <p className="text-xs">
                {patient?.dosespotError?.details ?? ''}
              </p>
            </Alert>
            <Tooltip
              title={noKnownDrugAllergiesTooltip}
              hide={isEmptyString(noKnownDrugAllergiesTooltip)}
            >
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={noKnownDrugAllergies}
                    onChange={(event) => setNoKnownDrugAllergies(event.target.checked)}
                    disabled={isNoKnownDrugAllergiesDisabled}
                  />
                  )}
                label="No Known Drug Allergies"
              />
            </Tooltip>
            <SearchableTextBoxAllergies
              option={drugAllergySettingsOptions}
              id="drug-allergic"
              placeholder="Select Drug Allergy"
              onChange={(value) => onChangeSelectAutoComplete(SelectReaction.allergic, value)}
              value={drugAllergic}
              reaction={SelectReaction.drugReaction}
              allergyReactionSettingsOptions={allergyReactionSettingsOptions}
              addButtonLabel="Add Drug Allergy"
              disabled={hasDosespotError || noKnownDrugAllergies}
              disabledTooltip={noKnownDrugAllergies
                ? 'Uncheck No Known Drug Allergies to add a drug allergy.'
                : undefined}
            />

            <h3 className="heading-sub-text mt-4">Non-Drug Related Allergies</h3>
            <SearchableTextBoxAllergies
              option={nonDrugAllergySettingsOptions}
              id="non-drug-allergic"
              placeholder="Select Non Drug Allergy"
              onChange={(value) => onChangeSelectAutoComplete(
                SelectReaction.NonDrugAllergic,
                value,
              )}
              value={nonDrugAllergic}
              reaction={SelectReaction.nonDrugReaction}
              allergyReactionSettingsOptions={allergyReactionSettingsOptions}
              addButtonLabel="Add Non Drug Allergy"
            />
          </Stack>
        </Box>
        <div className="next-button-fixed">
          <Button onClick={() => clickNextOrSubmit(nextPage)}>
            Submit
          </Button>
        </div>
      </Box>
    </>
  );
}

export default AllergiesScreen;
